import { useState, useEffect } from 'react';

// ============================================================================
// USE LOCAL SETTINGS HOOK - LOCALSTORAGE AYARLARI
// ============================================================================

export interface RingSoundOption {
  value: string;
  label: string;
  frequency: number;
  pattern: number[];
}

export interface UseLocalSettingsReturn {
  /** Telefon zil sesi açık mı? */
  phoneRingSoundEnabled: boolean;
  setPhoneRingSoundEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  /** Seçili zil sesi */
  selectedRingSound: string;
  setSelectedRingSound: React.Dispatch<React.SetStateAction<string>>;
  /** Gelen arama popup modalı açık mı? */
  incomingCallModalEnabled: boolean;
  setIncomingCallModalEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  /** Zil sesi seçenekleri */
  ringSoundOptions: RingSoundOption[];
}

/**
 * localStorage'dan ayarları yükler ve senkronize tutar
 */
export const useLocalSettings = (): UseLocalSettingsReturn => {
  // Telefon sesi ayarları (localStorage'dan yükle)
  const [phoneRingSoundEnabled, setPhoneRingSoundEnabled] = useState<boolean>(() => {
    try {
      const saved = localStorage.getItem('packageOrder_phoneRingSoundEnabled');
      console.log('📦 [LocalSettings] phoneRingSoundEnabled yükleniyor:', saved);
      if (saved !== null) {
        return JSON.parse(saved) === true;
      }
      return false;
    } catch (e) {
      console.error('📦 [LocalSettings] phoneRingSoundEnabled yükleme hatası:', e);
      return false;
    }
  });
  
  const [selectedRingSound, setSelectedRingSound] = useState<string>(() => {
    try {
      const saved = localStorage.getItem('packageOrder_selectedRingSound');
      console.log('📦 [LocalSettings] selectedRingSound yükleniyor:', saved);
      return saved || 'ring1';
    } catch (e) {
      console.error('📦 [LocalSettings] selectedRingSound yükleme hatası:', e);
      return 'ring1';
    }
  });
  
  // Gelen arama modalı ayarı (localStorage'dan yükle)
  const [incomingCallModalEnabled, setIncomingCallModalEnabled] = useState<boolean>(() => {
    try {
      const saved = localStorage.getItem('packageOrder_incomingCallModalEnabled');
      console.log('📦 [LocalSettings] incomingCallModalEnabled yükleniyor:', saved);
      if (saved !== null) {
        return JSON.parse(saved) === true;
      }
      return false;
    } catch (e) {
      console.error('📦 [LocalSettings] incomingCallModalEnabled yükleme hatası:', e);
      return false;
    }
  });
  
  // Zil sesi seçenekleri (Web Audio API ile sentetik ses)
  const ringSoundOptions: RingSoundOption[] = [
    { value: 'ring1', label: 'Klasik Telefon', frequency: 425, pattern: [1000, 2000] },
    { value: 'ring2', label: 'Dijital Zil', frequency: 800, pattern: [400, 200, 400, 1000] },
    { value: 'ring3', label: 'Melodi', frequency: 587, pattern: [200, 100, 200, 100, 200, 500] },
    { value: 'ring4', label: 'Alarm', frequency: 1000, pattern: [150, 150] },
    { value: 'ring5', label: 'Soft Zil', frequency: 350, pattern: [800, 1500] },
  ];
  
  // Ayarları localStorage'a kaydet
  useEffect(() => {
    try {
      localStorage.setItem('packageOrder_phoneRingSoundEnabled', JSON.stringify(phoneRingSoundEnabled));
      console.log('📦 [LocalSettings] phoneRingSoundEnabled kaydedildi:', phoneRingSoundEnabled);
    } catch (e) {
      console.error('📦 [LocalSettings] phoneRingSoundEnabled kaydetme hatası:', e);
    }
  }, [phoneRingSoundEnabled]);
  
  useEffect(() => {
    try {
      localStorage.setItem('packageOrder_selectedRingSound', selectedRingSound);
      console.log('📦 [LocalSettings] selectedRingSound kaydedildi:', selectedRingSound);
    } catch (e) {
      console.error('📦 [LocalSettings] selectedRingSound kaydetme hatası:', e);
    }
  }, [selectedRingSound]);
  
  useEffect(() => {
    try {
      localStorage.setItem('packageOrder_incomingCallModalEnabled', JSON.stringify(incomingCallModalEnabled));
      console.log('📦 [LocalSettings] incomingCallModalEnabled kaydedildi:', incomingCallModalEnabled);
    } catch (e) {
      console.error('📦 [LocalSettings] incomingCallModalEnabled kaydetme hatası:', e);
    }
  }, [incomingCallModalEnabled]);
  
  return {
    phoneRingSoundEnabled,
    setPhoneRingSoundEnabled,
    selectedRingSound,
    setSelectedRingSound,
    incomingCallModalEnabled,
    setIncomingCallModalEnabled,
    ringSoundOptions,
  };
};

export default useLocalSettings;
